import { roundClient } from "./policy";
import { normalizeUnit } from "./units";
import type { ContractorTerms, MarkupPolicy } from "./types";

/**
 * Цена клиенту по расчёту подрядчика.
 *
 * Два режима, и разница между ними — та самая ловушка, о которой ТЗ §6.5
 * предупреждает отдельно:
 *
 *   наценка 40%  →  цена = расход × 1,4        →  рентабельность 28,6%
 *   маржа 40%    →  цена = расход / (1 − 0,4)  →  рентабельность 40%
 *
 * Аня говорит «добавь 40% наценки» и имеет в виду первое. Плановая
 * рентабельность подразделения — второе. Путать их дорого, поэтому режим
 * хранится явно, а интерфейс всегда показывает получившуюся рентабельность.
 */
export function contractorPrice(cost: number, terms: ContractorTerms, step = 100): number {
  if (cost <= 0) return 0;
  const raw =
    terms.mode === "markup"
      ? cost * (1 + terms.rate)
      : terms.rate >= 1
        ? cost
        : cost / (1 - terms.rate);
  // Клиентские суммы округляем до сотни — как Аня и описывала.
  return roundClient(raw, step);
}

/** Какая рентабельность получится при выбранном режиме и доле. */
export function contractorMargin(terms: ContractorTerms): number {
  if (terms.mode === "margin") return terms.rate;
  return terms.rate / (1 + terms.rate);
}

/**
 * Наценка по умолчанию для единицы измерения.
 *
 * По архиву они разные: часы перепродают за 1,54×, штуки за 2,60×, комплексы
 * за 1,90×. Подставляется системой, менеджер правит.
 */
export function defaultMarkupRate(unit: string, markup: MarkupPolicy): number {
  // Единица и ключи политики сравниваются нормализованными: «шт.» из промпта
  // разбора и «Шт» из рук менеджера — те же штуки, что «шт» в политике.
  // Иначе тираж шоколадок уходил клиенту с наценкой «по умолчанию» 0,82
  // вместо 1,6 на штуки — и это никто не видел.
  const key = normalizeUnit(unit);
  const hit = Object.entries(markup.byUnit).find(([k]) => normalizeUnit(k) === key);
  return hit ? hit[1] : markup.default;
}
